export const DEFAULT_BRAND_COLOR = "#31628d";

interface RgbColor {
    r: number;
    g: number;
    b: number;
}

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

const parseHex = (value: string): RgbColor => ({
    r: Number.parseInt(value.slice(1, 3), 16),
    g: Number.parseInt(value.slice(3, 5), 16),
    b: Number.parseInt(value.slice(5, 7), 16),
});

const toHex = ({ r, g, b }: RgbColor): string =>
    `#${[r, g, b].map((value) => value.toString(16).padStart(2, "0")).join("")}`;

const mix = (color: RgbColor, target: RgbColor, amount: number): RgbColor => ({
    r: Math.round(color.r + (target.r - color.r) * amount),
    g: Math.round(color.g + (target.g - color.g) * amount),
    b: Math.round(color.b + (target.b - color.b) * amount),
});

const relativeLuminance = ({ r, g, b }: RgbColor): number => {
    const channel = (component: number): number => {
        const normalized = component / 255;
        return normalized <= 0.03928
            ? normalized / 12.92
            : ((normalized + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

const contrastWithWhite = (color: RgbColor): number =>
    1.05 / (relativeLuminance(color) + 0.05);

const ensureLightTextContrast = (value: string): string => {
    let color = parseHex(value);
    let attempts = 0;
    while (contrastWithWhite(color) < 4.5 && attempts < 24) {
        color = mix(color, { r: 0, g: 0, b: 0 }, 0.08);
        attempts += 1;
    }
    return toHex(color);
};

export const normalizeBrandColor = (value?: string | null): string => {
    const candidate = HEX_COLOR.test(value || "")
        ? (value as string)
        : DEFAULT_BRAND_COLOR;
    return ensureLightTextContrast(candidate);
};

export const applyBrandTheme = (value?: string | null): void => {
    const color = normalizeBrandColor(value);
    const rgb = parseHex(color);
    const root = document.documentElement;
    const rgbValue = `${rgb.r} ${rgb.g} ${rgb.b}`;

    root.style.setProperty("--app-color", color);
    root.style.setProperty("--app-color-rgb", rgbValue);
    root.style.setProperty("--color-primary-rgb", rgbValue);
    root.style.setProperty(
        "--app-color-hover",
        toHex(mix(rgb, { r: 0, g: 0, b: 0 }, 0.22))
    );
    root.style.setProperty(
        "--app-color-active",
        toHex(mix(rgb, { r: 255, g: 255, b: 255 }, 0.18))
    );
    root.style.setProperty(
        "--app-color-light",
        toHex(mix(rgb, { r: 255, g: 255, b: 255 }, 0.94))
    );
    root.style.setProperty(
        "--app-color-gradient",
        toHex(mix(rgb, { r: 255, g: 255, b: 255 }, 0.9))
    );
    root.style.setProperty("--app-color-selected", `rgba(${rgbValue} / 0.8)`);
    let darkForeground = rgb;
    const darkLuminance = relativeLuminance({ r: 47, g: 47, b: 53 });
    for (
        let attempt = 0;
        attempt < 24 &&
        (relativeLuminance(darkForeground) + 0.05) / (darkLuminance + 0.05) <
            4.5;
        attempt++
    ) {
        darkForeground = mix(darkForeground, { r: 255, g: 255, b: 255 }, 0.1);
    }
    root.style.setProperty("--app-color-on-dark", toHex(darkForeground));
};
